import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import ConsultationModal from "@/components/ConsultationModal";
import { Globe, Heart, Users, Shield, Clock, Award, ArrowRight, CheckCircle, Eye, Leaf, Compass } from "lucide-react";

const CONSULTANT_IMG = "https://images.unsplash.com/photo-1758691462858-f1286e5daf40?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

const brandPillars = [
  { icon: Shield, title: "Ethical Facilitation", desc: "No false guarantees, no doctor interference, no mis-selling. Trust is the product. We coordinate care journeys while clinical decisions remain solely with the treating doctors." },
  { icon: Award, title: "Curated Clinical Excellence", desc: "Hospitals chosen for capability, systems, and patient handling -- not just famous names. Every partner undergoes rigorous quality evaluation." },
  { icon: Leaf, title: "Integrative Recovery", desc: "Not either/or. The right blend of Allopathy, Ayurveda, and Wellness for each patient journey. Modern surgery combined with traditional healing wisdom." },
  { icon: Compass, title: "Travel & Logistics Mastery", desc: "Visa, stay, local support, language -- so the patient can focus on healing. Every detail handled with precision." },
];

const careJourney = [
  { step: "01", title: "Plan with Clarity", desc: "Case intake, reports review, right specialist mapping, expected pathways, and transparent estimate guidance." },
  { step: "02", title: "Prepare & Protect", desc: "Visa/documentation support, travel logistics, language/cultural support, privacy-first handling of records." },
  { step: "03", title: "Treat with Trust", desc: "Hospital coordination, appointments/admission, ethical communication with no exaggerated outcome claims." },
  { step: "04", title: "Recover Holistically", desc: "Post-procedure recovery planning plus optional integrative wellness (Ayurveda/therapies) where appropriate." },
  { step: "05", title: "Continue After Return", desc: "Discharge summary coordination, follow-ups, rehab/wellness plan, and continuity touchpoints." },
];

const milestones = [
  { year: "2016", event: "Founded as Raksaplava with base in India, serving patients primarily from Afghanistan." },
  { year: "2019", event: "Expanded operations to Middle East and African markets with growing patient trust." },
  { year: "2022", event: "Partnered with Bhaktivedanta Hospital, Apollo, Medanta for multi-specialty care." },
  { year: "2024", event: "Rebranded as Paxgrow India Pvt Ltd. Expanded to dental and skincare tourism with MK Aesthetics & Oramax." },
  { year: "2025", event: "Launched wellness tourism with Govardhan Eco-village. Entered USA and UK markets." },
  { year: "2026", event: "Now serving 15+ countries. Positioned as a Medical Value Travel facilitator." },
];

const services = [
  "Medical opinion and cost estimation",
  "Visa assistance and travel coordination",
  "Airport pickup and hotel accommodation",
  "Interpreter and translation services",
  "Hospital appointment scheduling",
  "Post-treatment follow-up care",
  "Telemedicine consultations",
  "Currency exchange assistance",
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="bg-gradient-to-b from-orange-50 to-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-orange-100 text-orange-700 border-orange-200 mb-4 font-body text-xs uppercase tracking-wider">About Paxgrow India</Badge>
              <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-slate-900 mb-4 leading-tight" data-testid="about-title">Care That Travels With You</h1>
              <p className="text-orange-600 font-medium text-sm mb-4 italic">
                PAX = Patient Assurance eXperience | GROW = Guided Recovery & Outcome Wellness
              </p>
              <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-4">
                Paxgrow is a Medical Value Travel facilitator delivering end-to-end care journeys -- from hospital excellence to holistic recovery -- built on transparency and ethical coordination.
              </p>
              <p className="text-slate-500 leading-relaxed mb-8 text-sm">
                Value = Clinical Quality + Cost Clarity + Time Efficiency + Recovery Experience + Continuity of Care
              </p>
              <ConsultationModal
                trigger={
                  <Button className="bg-orange-600 hover:bg-orange-700 text-white rounded-full px-8 py-6 font-semibold text-base" data-testid="about-cta-btn">
                    Start Your Journey <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                }
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img src={CONSULTANT_IMG} alt="Paxgrow Medical Consultation" className="w-full h-[450px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Origin Story */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl font-semibold text-slate-900 mb-8">Our Origin Story</h2>
          <div className="text-left space-y-4 text-slate-600 leading-relaxed">
            <p>Paxgrow was born from a simple observation: <strong className="text-slate-800">When someone needs treatment away from home, the hardest part isn't only the surgery or procedure -- it's the uncertainty.</strong></p>
            <p className="italic text-slate-500">Who do we trust? What will it cost? What happens after discharge? Who will coordinate? What if something goes wrong in a new country?</p>
            <p>So Paxgrow exists to make healthcare travel feel less like a gamble and more like a guided path:</p>
            <ul className="space-y-2 pl-4">
              <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-orange-600 mt-1 shrink-0" /><span><strong className="text-slate-800">Peace of mind</strong> before you travel</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-orange-600 mt-1 shrink-0" /><span><strong className="text-slate-800">Confidence</strong> while you're under care</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-orange-600 mt-1 shrink-0" /><span><strong className="text-slate-800">Recovery support</strong> after treatment</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-orange-600 mt-1 shrink-0" /><span>A <strong className="text-slate-800">holistic, human-first experience</strong> -- without fluff, without false promises</span></li>
            </ul>
          </div>
          <div className="mt-8 p-6 bg-orange-50 rounded-2xl">
            <p className="text-emerald-800 text-sm font-medium leading-relaxed">
              "We coordinate care journeys, documentation, and travel support -- while clinical decisions remain solely with the treating doctors and institutions."
            </p>
          </div>
        </div>
      </section>

      {/* Brand Pillars */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl font-semibold text-slate-900 text-center mb-14">Our Brand Pillars</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {brandPillars.map((p, i) => (
              <Card key={i} className="bg-white border border-slate-100 rounded-2xl p-8 card-hover" data-testid={`pillar-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center mb-4">
                  <p.icon className="h-6 w-6 text-orange-600" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">{p.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{p.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Care Journey */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge className="bg-orange-100 text-orange-700 border-orange-200 mb-4 font-body text-xs uppercase tracking-wider">The Paxgrow Care Journey</Badge>
            <h2 className="font-heading text-3xl font-semibold text-slate-900">End-to-End Care, Every Step</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {careJourney.map((s, i) => (
              <div key={i} className="text-center" data-testid={`care-step-${i}`}>
                <div className="w-14 h-14 rounded-full bg-orange-600 flex items-center justify-center mx-auto mb-4 shadow-md">
                  <span className="text-white font-bold text-sm">{s.step}</span>
                </div>
                <h3 className="font-heading text-base font-semibold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-slate-500 text-xs leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline + Services */}
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-8">Our Journey</h2>
              <div className="space-y-5">
                {milestones.map((m, i) => (
                  <div key={i} className="flex gap-4" data-testid={`milestone-${i}`}>
                    <div className="w-16 shrink-0"><span className="text-orange-600 font-bold">{m.year}</span></div>
                    <div className="border-l-2 border-orange-200 pl-4 pb-1"><p className="text-slate-600 text-sm">{m.event}</p></div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-8">Our Services</h2>
              <Card className="bg-white rounded-2xl p-8 border border-slate-100">
                <p className="text-slate-600 text-sm mb-5">All facilitation services are complimentary. Your hospital bill does not increase by using Paxgrow.</p>
                <ul className="space-y-3">
                  {services.map((s, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" /><span>{s}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Medical Panel */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl font-semibold text-slate-900 mb-14">Our Medical Panel</h2>
          <div className="max-w-lg mx-auto">
            <Card className="bg-white rounded-2xl border border-slate-100 overflow-hidden card-hover" data-testid="doctor-panel-card">
              <div className="h-48 bg-gradient-to-br from-orange-100 to-slate-50 flex items-center justify-center">
                <div className="w-24 h-24 rounded-full bg-orange-600 flex items-center justify-center text-2xl font-bold text-white">KG</div>
              </div>
              <div className="p-6">
                <h3 className="font-heading text-xl font-semibold text-slate-900">Dr. Khushal Gangwani</h3>
                <p className="text-orange-600 font-medium text-sm mb-2">Dental & Skincare Specialist</p>
                <p className="text-slate-500 text-sm leading-relaxed">Expert in cosmetic dentistry, dental implants, and advanced skincare treatments. Leading the dental and skincare division with MK Aesthetics and Oramax.</p>
              </div>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
